import { useState } from 'react'
import { useI18n, formatar } from '../lib/i18n'

/**
 * Exame de Classe C.
 *
 * Segue o wireframe (docs/wireframes.html, tela 09) e a Res. 777/2025:
 * progresso POR DISCIPLINA, nunca só a média — mostrar a média esconderia a
 * reprovação, já que a aprovação exige a maioria das questões em cada uma
 * (8 de 15 na Classe C, conforme a Cartilha do Serviço Radioamador).
 *
 * Uma questão por tela, alternativas empilhadas: em celular, quatro opções
 * lado a lado é erro de toque garantido.
 */

interface Question {
  id: string
  discipline: string
  stem: string
  options: string[]
}

interface Score {
  discipline: string
  name: string
  score: number
  passed: boolean
}

interface Reviewed {
  id: string
  discipline: string
  stem: string
  options: string[]
  correct: number
  chose: number
  right: boolean
  explanation: string
  reference: string
}

interface Result {
  passed: boolean
  scores: Score[]
  pass_mark: number
  review: Reviewed[]
  failed_in?: string[]
  message: string
  /** Indicativo emitido na aprovação — quem passa deixa de ser apelido. */
  indicativo?: string
}

export function Exam({ onApproved }: { onApproved?: () => void } = {}) {
  const { t } = useI18n()
  const DISC_NAME: Record<string, string> = {
    tecnica_etica: t.exam.tecnicaEtica,
    legislacao: t.exam.legislacao,
    eletronica: t.exam.eletronica,
  }
  const [attempt, setAttempt] = useState<string | null>(null)
  const [questions, setQuestions] = useState<Question[]>([])
  const [answers, setAnswers] = useState<Record<number, number>>({})
  const [at, setAt] = useState(0)
  const [result, setResult] = useState<Result | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const start = async () => {
    setBusy(true)
    setErr(null)
    try {
      const r = await fetch('/api/exams?class=C', { method: 'POST' })
      const d = (await r.json()) as {
        attempt_id?: string
        questions?: Question[]
        message?: string
      }
      if (!r.ok || !d.attempt_id) {
        setErr(d.message ?? t.exam.erroIniciar)
        return
      }
      setAttempt(d.attempt_id)
      setQuestions(d.questions ?? [])
      setAnswers({})
      setAt(0)
      setResult(null)
    } finally {
      setBusy(false)
    }
  }

  const choose = async (choice: number) => {
    if (!attempt) return
    setAnswers((a) => ({ ...a, [at]: choice }))
    await fetch('/api/exams/answer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attempt_id: attempt, index: at, choice }),
    })
    if (at < questions.length - 1) setAt(at + 1)
  }

  const finish = async () => {
    if (!attempt) return
    setBusy(true)
    try {
      const r = await fetch(`/api/exams/${attempt}/finish`, { method: 'POST' })
      const d = (await r.json()) as Result
      setResult(d)
      // A aprovação troca a identidade: o apelido vira indicativo emitido por
      // nós. Sem avisar a casca, a barra de estado ficaria com o nome antigo
      // até a próxima recarga.
      if (d.passed) onApproved?.()
    } finally {
      setBusy(false)
    }
  }

  // --- resultado ---
  if (result) {
    const wrong = result.review.filter((q) => !q.right)
    return (
      <section className="exam">
        <h2>{t.exam.titulo}</h2>
        <p className={result.passed ? 'exam-ok' : 'denial'}>{result.message}</p>

        <div className="exam-scores">
          {result.scores.map((s) => (
            <div key={s.discipline} className={s.passed ? '' : 'bad-disc'}>
              <span>{DISC_NAME[s.discipline] ?? s.name}</span>
              <div className="bar">
                <i style={{ width: `${s.score * 100}%` }} />
              </div>
              <b>{(s.score * 100).toFixed(0)}%</b>
            </div>
          ))}
        </div>
        <p className="exam-rule">
          {formatar(t.exam.examRule, { n: (result.pass_mark * 100).toFixed(0) })}
        </p>

        {wrong.length > 0 && (
          <>
            <h3>{t.exam.oQueVoceErrou}</h3>
            <ul className="exam-review">
              {wrong.slice(0, 5).map((q) => (
                <li key={q.id}>
                  <p className="stem">{q.stem}</p>
                  <p className="right">{t.exam.certa} {q.options[q.correct]}</p>
                  <p className="why">{q.explanation}</p>
                  <p className="ref">{q.reference}</p>
                </li>
              ))}
            </ul>
          </>
        )}

        <button onClick={() => void start()} disabled={busy}>
          {t.exam.fazerOutraProva}
        </button>
      </section>
    )
  }

  // --- prova em andamento ---
  if (attempt && questions.length > 0) {
    const q = questions[at]
    if (!q) return null
    const answered = Object.keys(answers).length

    return (
      <section className="exam">
        <div className="exam-top">
          <span>{DISC_NAME[q.discipline] ?? q.discipline}</span>
          <span>
            {at + 1} / {questions.length}
          </span>
        </div>

        <p className="exam-stem">{q.stem}</p>
        <div className="exam-options">
          {q.options.map((opt, i) => (
            <button
              key={i}
              className={answers[at] === i ? 'chosen' : ''}
              onClick={() => void choose(i)}
            >
              {String.fromCharCode(65 + i)} · {opt}
            </button>
          ))}
        </div>

        <div className="exam-nav">
          <button onClick={() => setAt(Math.max(0, at - 1))} disabled={at === 0}>
            {t.exam.anterior}
          </button>
          <button
            onClick={() => setAt(Math.min(questions.length - 1, at + 1))}
            disabled={at === questions.length - 1}
          >
            {t.exam.proxima}
          </button>
        </div>

        <button className="exam-submit" onClick={() => void finish()} disabled={busy}>
          {t.exam.entregarProva}
          <small>{formatar(t.exam.respondidasContagem, { respondidas: answered, total: questions.length })}</small>
        </button>
      </section>
    )
  }

  // --- convite ---
  return (
    <section className="exam">
      <h2>{t.exam.titulo}</h2>
      <p className="exam-intro">
        {t.exam.introPre} <b>{t.exam.introForte}</b> {t.exam.introPos}
      </p>
      {err && <p className="denial">{err}</p>}
      <button onClick={() => void start()} disabled={busy}>
        {t.exam.iniciarProva}
      </button>
    </section>
  )
}
